import React, { ChangeEvent, Suspense, useEffect, useRef, useState } from 'react';
import { useSpring, animated } from '@react-spring/web';
import {
  Box, Button, Card, CardActionArea, CardContent, CircularProgress,
  Dialog, Divider, IconButton, InputBase,
  Paper, Slide, styled, Toolbar, Typography
} from '@mui/material';
import { Close as CloseIcon, Download, Send as SendIcon } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { grey } from '@mui/material/colors';
import { postJSON, resolveAIUrl } from '../api';
import useSWR from 'swr';
import { getDocumentsByAI, getDocumentsByAIargs } from '../apiV2/ai';
import { format } from 'date-fns';
import { useHistory } from 'react-router';
import ErrorBoundary from '../components/errorBoundles';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const QusetionTypography = styled(Typography)(() => ({
  backgroundColor: grey[100],
}));

interface AnswersProps {
  answer: AIanswer;
}
function AnswersComp(props: AnswersProps) {
  const { answer } = props;
  const { message } = answer;

  let full_context = '';
  for (const item of message) {
    full_context += item.data.d ?? '';
  }

  return (
    <Box p={1}>
      <Typography variant='body1' whiteSpace='pre-line'>
        {full_context}
      </Typography>
    </Box>
  );
}

interface RelatedFilesProps {
  tid: string;
  references: AIanswer['references'];
  onClickAsset: () => void;
  onScrollToBottom: () => void;
}
export function RelatedFiles(props: RelatedFilesProps) {
  const { tid, references, onClickAsset, onScrollToBottom } = props;
  const history = useHistory();

  if (references.length === 0 || references[0].data.length === 0) {
    return null;
  }

  const reference = references[0];
  const referenceData = reference.data;
  const astId = [];

  if (referenceData && referenceData.length > 0) {
    for (const item of referenceData) {
      astId.push(item.fid);
    }
  }

  const { data: documents } = useSWR(
    getDocumentsByAIargs({ tid, astId }),
    getDocumentsByAI,
    {
      revalidateOnMount: true,
      onSuccess: () => {onScrollToBottom();},
      onError: () => {onScrollToBottom();},
    }
  );

  const handleClick = (document) => {
    onClickAsset();
    history.push(`/assets/${document.id}`);
  };

  if ((documents ?? null) === null) {
    return null;
  }

  return (
    <Box component={Paper} p={2} my={2}>
      <Typography variant='body1' fontWeight='bold' gutterBottom>
        相关资料
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {documents.map(document =>
        <Card key={document.id} sx={{ mb: 1 }}>
          <CardActionArea
            onClick={() => handleClick(document)}
          >
            <CardContent
              style={{
                display: 'flex', justifyContent: 'flex-start', alignItems: 'center',
                padding: '8px', borderBottom: '1px solid #F6F6F6'
              }}
            >
              <img src={document.thumbnail}
                style={{
                  height: '30px', width: '30px',
                  marginLeft: '12px',
                  marginRight: '12px',
                  objectFit: 'cover'
                }}
              />
              <Box display="flex" flexDirection="column" overflow="hidden" flex={1} >
                <Typography
                  variant='body2'
                >
                  {document.name}
                </Typography>
                {document.updatedAt && <Typography  variant="caption" color="textSecondary">
                  {format(new Date(document.updatedAt), 'yyyy-MM-dd')}
                </Typography>}
              </Box>

              {/* 没有下载链接 */}
              {false && <IconButton size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  window.open(document.asset.url, "_blank");
                }}
              >
                <Download />
              </IconButton>}
            </CardContent>
          </CardActionArea>
        </Card>
      )}
    </Box>
  );
}

interface QAitemProps {
  tid: string;
  item: QAList;
  onClickAsset: () => void;
  onScrollToBottom: () => void;
}
function QAitem(props: QAitemProps) {
  const { tid, item, onClickAsset, onScrollToBottom } = props;
  const { question, answer } = item;
  const formatAnswer = {
    message: [],
    references: [],
    end: []
  } as AIanswer;

  if (answer.length !== 0) {
    const chunks = answer.split(/\n\n/);
    for (const chunk of chunks) {
      const { type, data } = processEventChunk(chunk);
      formatAnswer[type].push(data);
    }
  }

  function safeParse(jsonString) {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      return '';
    }
  }

  function processEventChunk(chunk) {
    const event = {
      type: 'message', // 默认类型
      data: '',
      id: null,
      retry: null
    };
    // 按行解析事件字段
    chunk.split('\n').forEach(line => {
      if (line.startsWith('event:')) {
        event.type = line.replace('event:', '').trim();
      } else if (line.startsWith('data:')) {
        event.data += line.replace('data:', '').trim() + '\n';
      } else if (line.startsWith('id:')) {
        event.id = line.replace('id:', '').trim();
      } else if (line.startsWith('retry:')) {
        event.retry = parseInt(line.replace('retry:', '').trim(), 10);
      }
    });

    // 清理数据末尾的换行符
    const trimData = event.data.trim();
    const jsonData = safeParse(trimData);

    return {
      type: event.type,
      data: {
        id: event.id,
        retry: event.retry,
        data: jsonData,
      }
    };

  }

  return (
    <Box>
      <Box mb={1}>
        <QusetionTypography display='inline-block' variant='body1'
          sx={{ py: 1, px: 1, borderRadius: 1 }}
        >
          {question}
        </QusetionTypography>
      </Box>
      <Box mb={1}>
        <AnswersComp answer={formatAnswer}/>
      </Box>
      <Suspense fallback={<Box></Box>}>
        {item.done && <RelatedFiles tid={tid}
          references={formatAnswer.references}
          onClickAsset={onClickAsset}
          onScrollToBottom={onScrollToBottom}
        />}
      </Suspense>
    </Box>
  );
}

interface AICompProps {
  tid: string;
  deviceType: 'computer' | 'mobile';
  toggleOpen: () => void;
  onClose?: () => void;
}
export function AIComp(props: AICompProps) {
  const {
    tid, deviceType = 'computer',
    toggleOpen, onClose = () => null,
  } = props;

  const messageEndRef = useRef(null);
  const [value, setValue] = useState<string>(null);
  const [isIMEComposing, setIsIMEComposing] = useState<boolean>(false);
  const [qaList, setQAList] = useState<QAList[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [answerBuffer, setAnswerBuffer] = useState('');
  const [sendEvent, setSendEvent] = useState<boolean>(false);
  const [scrollBottom, setScrollBottom] = useState<boolean>(false);

  useEffect(() => {
    if (answerBuffer) {
      setQAList((prev) => {
        const answer = prev[prev.length - 1].answer + answerBuffer;
        prev[prev.length - 1].answer = answer;
        return prev;
      });
      setAnswerBuffer('');
    }
  }, [answerBuffer]);

  useEffect(() => {
    if (sendEvent && qaList.length > 0 && qaList[qaList.length - 1].done) {
      const QaItem = qaList[qaList.length -1];
      const { question, answer } = QaItem;

      const formatAnswer = {
        message: [],
        references: [],
        end: []
      } as AIanswer;

      if (answer.length !== 0) {
        const chunks = answer.split(/\n\n/);
        for (const chunk of chunks) {
          const { type, data } = processEventChunk(chunk);
          formatAnswer[type].push(data);
        }
      }

      const body = {
        question,
        source: 'ChatTokenUsage',
        ...(formatAnswer.end[0]?.data || {})
      };
      handleSendEvent(body).finally(() => {
        setSendEvent(false);
      });
    }
  }, [qaList, sendEvent]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [qaList]);

  useEffect(() => {
    if (scrollBottom) {
      messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setScrollBottom(false);
    }
  }, [scrollBottom]);


  const handleFetchAnswer = async (question: string) => {
    setIsLoading(true);
    const New_QaId = qaList.length + 1;
    const newQuestion: QAList = {
      id: New_QaId,
      question: question,
      answer: '',
      done: false,
    };
    setQAList(prev => [...prev, newQuestion]);
    try {
      const url = `${tid}/aidoc/chat`;
      const res = await fetch(
        resolveAIUrl(url),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ question: question })
        }
      );

      // 停止生成可使用 await reader.cancel / closed
      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        setAnswerBuffer(prev => prev + chunk);
      }
    } catch (err) {
      console.error('ERR', err);
    } finally {
      setQAList((prev) => {
        prev[prev.length - 1].done = true;
        return prev;
      });
      setSendEvent(true);
      setIsLoading(false);
    }
  };

  const handleSendEvent = async(
    body: { question: string, ir_req: number, ir_res: number, cg_req: number, cg_res: number }
  ) => {
    try {
      const url = `stats/${tid}/aiConsumptionEvent`;
      await postJSON({
        url,
        body
      });
    } catch (err) {
      console.error('ERR', err);
    }
  };

  const handleInput = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const trimValue = event.target.value.trim();
    setValue(trimValue === '' ? null : event.target.value);
  };

  const handleSubmit = () => {
    if (value !== null) {
      setScrollBottom(true);
      handleFetchAnswer(value);
      setValue(null);
    }
  };

  const handleCompositionStart = () => {
    setIsIMEComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsIMEComposing(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !isIMEComposing) {
      event.preventDefault();
      handleFetchAnswer(value);
      setValue(null);
    }
  };

  const handleScrollToBottom = () => {
    setTimeout(() => {
      setScrollBottom(true);
    }, 100);
  };

  const InputPlaceholder = deviceType === 'computer'
    ? '输入您想知道的问题，使用Shift + Enter 换行'
    : '输入您想知道的问题';

  return (
    <Box display='flex' flexDirection='column'
      width='100%' height='100%'
    >
      <Toolbar disableGutters
        sx={{
          display: 'flex',
          flexGrow: 0,
        }}
      >
        <Typography variant='h6' sx={{ px: 2 }}>
          AI求索
        </Typography>
        <IconButton onClick={toggleOpen}
          sx={{
            position: 'absolute',
            right: 8, top: 8,
            color: grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>
      <Divider />
      <Box display='flex' flexGrow={1} overflow='auto' sx={{ overflowX: 'hidden' }}>
        <Box width='100%' p={2} >
          {qaList.map(item => <QAitem key={`qaList_${item.id}`}
            tid={tid} item={item}
            onClickAsset={onClose}
            onScrollToBottom={handleScrollToBottom}
          />)}
          <Box height={2} mb={2} ref={messageEndRef} />
        </Box>
      </Box>
      <Box flexGrow={0} display='flex' position='relative' flexDirection='column'
        boxSizing='border-box' width='100%' p={1}
        sx={{ cursor: 'text' }}
      >
        <Box component={Paper} elevation={3}
          display='flex' flexDirection='column' justifyContent='flex-start'
          overflow='hidden' borderRadius={1} p={1}
        >
          <InputBase multiline value={value ?? ''}
            placeholder={InputPlaceholder}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
          />
          <Box mt={.5} display='flex' flexDirection='row' justifyContent='flex-end' >
            <Button variant='contained' size='medium' disabled={isLoading}
              endIcon={<SendIcon />} onClick={handleSubmit}
              sx={{
                position: 'relative',
                py: .8, px: .5,
                minWidth: 50,
                '& .MuiButton-icon': {
                  mx: 0
                }
              }}
            >
              {isLoading && <CircularProgress size={26} color='primary'
                sx={{
                  position: 'absolute',
                  top: '50%', left: '50%',
                  mt: '-13px', ml: '-13px'
                }}
              />}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

interface MobileAIProps {
  tid: string;
}
export function MobileAI(props: MobileAIProps) {
  const { tid } = props;

  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen(!open);

  const buttonStyles = useSpring({
    to: { opacity: open ? 0 : 1, transform: open ? 'scale(0)' : 'scale(1)' },
    from: { opacity: 1, transform: 'scale(1)' },
    config: { duration: 150 }
  });

  return (
    <Box position='fixed' bottom={20} right={20} zIndex={9999}
      width={ open ? '0px' : '60px' } height={ open ? '0px' : '60px' }
    >
      <animated.button
        style={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          width: '60px',
          height: '60px',
          cursor: 'pointer',
          border: 'none',
          backgroundColor:'transparent',
          backgroundImage: 'url(/images/AI/bg.png)',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          ...buttonStyles
        }}
        className="bg-blue-500 text-white p-2 rounded-full focus:outline-none"
        onClick={toggleOpen}
      >
        <img src="/images/AI/animate.gif"
          alt="Animated Icon"
          style={{
            width: '34px',
            height: '34px',
            marginBottom: '6px'
          }}
        />
      </animated.button>
      <Dialog fullScreen open={open}
        onClose={toggleOpen}
        TransitionComponent={Transition}
      >
        <Box height='100%'>
          <ErrorBoundary>
            <AIComp
              tid={tid}
              deviceType={'mobile'}
              toggleOpen={toggleOpen}
              onClose={() => setOpen(false)}
            />
          </ErrorBoundary>
        </Box>
      </Dialog>
    </Box>
  );
}
interface DesktopProps {
  tid: string;
}
export function DesktopAI(props: DesktopProps) {
  const { tid } = props;

  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen(!open);

  const buttonStyles = useSpring({
    to: { opacity: open ? 0 : 1, transform: open ? 'scale(0)' : 'scale(1)' },
    from: { opacity: 1, transform: 'scale(1)' },
    config: { duration: 350 }
  });

  const dialogStyles = useSpring({
    scale: open ? 1 : 0,
    borderRadius: open ? '8px' : '50%',
    opacity: open ? 1 : 0,
    transformOrigin: 'bottom right',
    config: { tension: 300, friction: 20, duration: 350 },
  });

  return (
    <Box position='fixed' bottom={40} right={40} zIndex={9999}
      width={ open ? '500px' : '100px' } height={ open ? '600px' : '100px' }
    >
      <animated.button
        style={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          width: '100px',
          height: '100px',
          cursor: 'pointer',
          border: 'none',
          backgroundColor:'transparent',
          backgroundImage: 'url(/images/AI/bg.png)',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          ...buttonStyles
        }}
        className="bg-blue-500 text-white p-2 rounded-full focus:outline-none"
        onClick={toggleOpen}
      >
        <img src="/images/AI/animate.gif"
          alt="Animated Icon"
          style={{
            width: '60px',
            height: '60px',
            marginBottom: '12px'
          }}
        />
      </animated.button>
      <animated.div
        style={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          width: '600px',
          height: 'calc(100vh - 136px)',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          overflow: 'hidden',
          ...dialogStyles
        }}
      >
        <ErrorBoundary>
          <AIComp
            tid={tid}
            deviceType={'computer'}
            toggleOpen={toggleOpen}
          />
        </ErrorBoundary>
      </animated.div>
    </Box>
  );
}


function safeParse(jsonString) {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    return '';
  }
}

function processEventChunk(chunk) {
  const event = {
    type: 'message', // 默认类型
    data: '',
    id: null,
    retry: null
  };
  // 按行解析事件字段
  chunk.split('\n').forEach(line => {
    if (line.startsWith('event:')) {
      event.type = line.replace('event:', '').trim();
    } else if (line.startsWith('data:')) {
      event.data += line.replace('data:', '').trim() + '\n';
    } else if (line.startsWith('id:')) {
      event.id = line.replace('id:', '').trim();
    } else if (line.startsWith('retry:')) {
      event.retry = parseInt(line.replace('retry:', '').trim(), 10);
    }
  });

  // 清理数据末尾的换行符
  const trimData = event.data.trim();
  const jsonData = safeParse(trimData);

  return {
    type: event.type,
    data: {
      id: event.id,
      retry: event.retry,
      data: jsonData,
    }
  };

}

interface QAList {
  id: number;
  question: string;
  answer: string;
  done: boolean;
}
interface AIanswer {
  message: {
    id: string,
    retry: string,
    data: { d: string },
  }[],
  references: {
    id: string,
    retry: string,
    data: { idx: number, fid: string }[],
  }[],
  end: {
    id: string,
    retry: string,
    data: {
      cg_req: number;
      cg_res: number;
      ir_req: number;
      ir_res: number;
    }
  }[]
}
interface AIProps {
  tid: string;
  deviceType: 'mobile' | 'computer';
}
export default function AI(props: AIProps) {
  const { tid = 'e5127588-684c-40de-8d16-6ee235e09b18', deviceType } = props;

  if (deviceType === 'mobile') {
    return (
      <MobileAI tid={tid} />
    );
  }

  return (
    <DesktopAI tid={tid} />
  );
}